import React, { useEffect, useRef } from 'react';
import maplibregl from 'maplibre-gl';
import 'maplibre-gl/dist/maplibre-gl.css';

const LIGHT_STYLE_URL = 'https://basemaps.cartocdn.com/gl/positron-gl-style/style.json';

/**
 * Inline map with a single draggable pin used during venue onboarding.
 *
 * - When `latitude`/`longitude` change from outside (e.g. address chosen
 *   in the autocomplete), the map flies to the new coords and moves the pin.
 * - When the user drags the pin, `onChange(lat, lng)` is fired.
 */
export function PinConfirmMap({
  latitude,
  longitude,
  onChange,
  height = 260,
  testId = 'venue-pin-confirm-map',
}) {
  const containerRef = useRef(null);
  const mapRef = useRef(null);
  const markerRef = useRef(null);
  const lastSet = useRef({ lat: null, lng: null });

  // Initialise once.
  useEffect(() => {
    if (!containerRef.current || mapRef.current) return undefined;

    const map = new maplibregl.Map({
      container: containerRef.current,
      style: LIGHT_STYLE_URL,
      center: [
        typeof longitude === 'number' ? longitude : 23.7275,
        typeof latitude === 'number' ? latitude : 37.9838,
      ],
      zoom: typeof latitude === 'number' && typeof longitude === 'number' ? 15.4 : 5,
      attributionControl: false,
      dragRotate: false,
      pitchWithRotate: false,
      cooperativeGestures: false,
    });
    mapRef.current = map;

    const markerEl = document.createElement('div');
    markerEl.setAttribute('data-testid', `${testId}-pin`);
    markerEl.style.cssText = `
      width: 42px; height: 56px; display: flex; flex-direction: column; align-items: center;
      filter: drop-shadow(0 6px 12px rgba(20, 16, 50, 0.30));
      cursor: grab;
    `;
    markerEl.innerHTML = `
      <div style="width:42px;height:42px;border-radius:50%;border:3px solid #FFFFFF;background:linear-gradient(135deg,#C41230,#E8192C);display:flex;align-items:center;justify-content:center;">
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#FFFFFF" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round">
          <path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 1116 0z"/>
          <circle cx="12" cy="10" r="3"/>
        </svg>
      </div>
      <div style="width:0;height:0;border-left:8px solid transparent;border-right:8px solid transparent;border-top:10px solid #C41230;margin-top:-4px;"></div>
    `;

    const marker = new maplibregl.Marker({ element: markerEl, anchor: 'bottom', draggable: true })
      .setLngLat([
        typeof longitude === 'number' ? longitude : 23.7275,
        typeof latitude === 'number' ? latitude : 37.9838,
      ])
      .addTo(map);
    markerRef.current = marker;

    marker.on('dragstart', () => { markerEl.style.cursor = 'grabbing'; });
    marker.on('dragend', () => {
      markerEl.style.cursor = 'grab';
      const lngLat = marker.getLngLat();
      lastSet.current = { lat: lngLat.lat, lng: lngLat.lng };
      onChange?.(lngLat.lat, lngLat.lng);
    });

    // Tap anywhere on the map to move the pin to that point.
    map.on('click', (event) => {
      marker.setLngLat(event.lngLat);
      lastSet.current = { lat: event.lngLat.lat, lng: event.lngLat.lng };
      onChange?.(event.lngLat.lat, event.lngLat.lng);
    });

    const handleResize = () => map.resize();
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      marker.remove();
      map.remove();
      mapRef.current = null;
      markerRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // When parent updates lat/lng, fly the pin there.
  useEffect(() => {
    if (!mapRef.current || !markerRef.current) return;
    if (typeof latitude !== 'number' || typeof longitude !== 'number') return;
    // Skip if the change came from our own drag (avoid feedback loop).
    if (lastSet.current.lat === latitude && lastSet.current.lng === longitude) return;
    markerRef.current.setLngLat([longitude, latitude]);
    mapRef.current.flyTo({
      center: [longitude, latitude],
      zoom: 15.6,
      duration: 800,
      essential: true,
    });
    lastSet.current = { lat: latitude, lng: longitude };
  }, [latitude, longitude]);

  return (
    <div
      data-testid={testId}
      style={{
        width: '100%',
        height,
        borderRadius: 20,
        overflow: 'hidden',
        position: 'relative',
        border: '1.5px solid var(--card-border)',
        boxShadow: '0 12px 28px rgba(20, 16, 50, 0.12)',
        background: 'var(--surface-2)',
      }}
    >
      <div ref={containerRef} style={{ width: '100%', height: '100%' }} />
      <div
        style={{
          position: 'absolute',
          left: 12,
          bottom: 12,
          padding: '6px 10px',
          borderRadius: 999,
          background: 'rgba(20, 16, 50, 0.78)',
          color: '#FFFFFF',
          fontFamily: "'Inria Serif', serif",
          fontSize: 11.5,
          fontWeight: 700,
          letterSpacing: '0.02em',
          backdropFilter: 'blur(6px)',
        }}
      >
        Drag the pin or tap to fine-tune
      </div>
    </div>
  );
}
